import React, { useState } from 'react';
import { AgentAvatar, PALETTES, parseSeed } from './AgentAvatar';
import { useWallet } from '../hooks/useWallet';
import { useModalAlert } from './ModalAlert';
import { portfolioFetch } from '../utils/portfolioFetch';
import { PortfolioTypeBadge } from './shared/PortfolioTypeBadge';
import { formatCurrency } from '../utils/formatting';

const AgentChip = ({ agent }) => {
  const { avatarSeed, paletteIndex } = parseSeed(agent.avatar_seed);
  return (
    <div className="flex items-center gap-1.5 bg-gray-700/60 rounded-full px-2 py-0.5 min-w-0">
      <AgentAvatar
        name={agent.name}
        seed={avatarSeed}
        colors={PALETTES[paletteIndex] || PALETTES[0]}
        size={16}
      />
      <span className="text-xs text-cyan-300 truncate">{agent.name}</span>
    </div>
  );
};

export const PortfolioSummaryCard = ({ portfolio, config, onClick, observerRole, onDelete = null }) => {
  const agent = portfolio.active_agent;
  const pnl = portfolio.total_pnl ?? null;
  const pnlPositive = pnl !== null && parseFloat(pnl) >= 0;

  const { jwtToken } = useWallet();
  const { showWarning, showError, showSuccess } = useModalAlert();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation(); // Prevent card click

    showWarning(
      'Delete Portfolio',
      `Are you sure you want to delete portfolio "${portfolio.name}"?\n\nThis action cannot be undone and will permanently remove all associated data.`,
      {
        showCancel: true,
        confirmText: 'Delete Portfolio',
        cancelText: 'Cancel',
        onConfirm: async () => {
          setIsDeleting(true);
          try {
            const apiBaseUrl = config?.apiBaseUrl || 'http://localhost:8000';
            const res = await portfolioFetch(`${apiBaseUrl}/portfolio/${portfolio.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json',
              },
            }, portfolio.id);

            if (!res.ok) {
              const errorData = await res.json().catch(() => ({}));
              throw new Error(errorData.detail || `Delete failed (${res.status})`);
            }

            showSuccess(`Portfolio "${portfolio.name}" has been deleted.`);
            if (onDelete) {
              onDelete(portfolio.id);
            }
          } catch (err) {
            console.error('Failed to delete portfolio:', err);
            showError(err.message);
          } finally {
            setIsDeleting(false);
          }
        }
      }
    );
  };

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer bg-gray-800 rounded-lg p-4 transition-all hover:shadow-lg ${
        !agent
          ? 'border border-amber-700/60 hover:border-amber-500'
          : 'border border-gray-700 hover:border-cyan-600 hover:shadow-cyan-900/20'
      }`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <h3 className="font-semibold text-white text-sm truncate">{portfolio.name}</h3>
        <div className="flex items-center gap-1 flex-shrink-0">
          <PortfolioTypeBadge portfolioType={portfolio.portfolio_type} isTestnet={portfolio.is_testnet} size="sm" />
          {observerRole && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-violet-900/40 text-violet-300 border border-violet-700/50">
              observer
            </span>
          )}
          {/* Delete button - only show for owned portfolios */}
          {!observerRole && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-1.5 rounded-md bg-red-900/40 text-red-400 hover:bg-red-900/60 hover:text-red-300 transition-colors disabled:opacity-50 flex-shrink-0"
              title="Delete portfolio"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="mb-2">
        {agent ? (
          <AgentChip agent={agent} />
        ) : (
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-xs text-amber-400 font-medium">Agent required</span>
          </div>
        )}
      </div>

      {!agent && (
        <p className="text-xs text-amber-300/70 mb-2">
          Assign a primary agent to enable this portfolio.
        </p>
      )}

      {pnl !== null && (
        <div className={`text-sm font-medium ${pnlPositive ? 'text-green-400' : 'text-red-400'}`}>
          {pnlPositive ? '+' : ''}{formatCurrency(pnl)}
        </div>
      )}
    </div>
  );
};
